import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Dialog, DialogActions, DialogContent, Button, Box, CircularProgress, Stack, Typography } from '@mui/material';
import DynamicForm from '../DynamicForm';
import JSONTableEditor from '../JSONTableEditor';
import FileUpload from '../FileUpload';
import CentralCalls from '../../centralCalls';

const FinishTaskDialog = ({
  open,
  onClose,
  title,
  phaseOid,
  lotOid,
  phaseType,
  onCompletePhaseClose,
  onSavePhaseSaveEdit,
  isUploading = false,
  dialogProps = {}
}) => {
  const finishTaskFilesRef = useRef(null);
  const [existingFiles, setExistingFiles] = useState([]);
  const [loadingExistingFiles, setLoadingExistingFiles] = useState(false);
  const [downloadingFileOid, setDownloadingFileOid] = useState(null);
  const [inlineImageUrls, setInlineImageUrls] = useState({});

  const procedureDetailsCompletionInsert = {
    procedureName: 'ui_sp_getPhaseCompletionPageDataInsert',
    parameters: { phase_oid: phaseOid, phase_type: phaseType, lot_oid: lotOid }
  };

  const procedureDetailsCompletion = {
    procedureName: 'ui_sp_getPhaseCompletionPageData',
    parameters: { phase_oid: phaseOid, lot_oid: lotOid }
  };

  const isImageFile = useCallback((file) => {
    const contentType = String(file?.content_type || '').toLowerCase();
    if (contentType.startsWith('image/')) return true;

    const fileName = String(file?.file_name || '').toLowerCase();
    return /\.(jpg|jpeg|png|gif|webp|bmp|heic|heif)$/i.test(fileName);
  }, []);

  const fetchExistingFiles = useCallback(async () => {
    if (!phaseOid || !lotOid) {
      setExistingFiles([]);
      return;
    }

    setLoadingExistingFiles(true);
    try {
      const rows = await CentralCalls.tableSelect('files', {
        phase_oid: Number(phaseOid),
        lot_oid: Number(lotOid),
        is_deleted: 0,
      });

      const files = Array.isArray(rows)
        ? rows
            .slice()
            .sort((a, b) => {
              const da = a?.created_at ? new Date(a.created_at).getTime() : 0;
              const db = b?.created_at ? new Date(b.created_at).getTime() : 0;
              return db - da;
            })
        : [];

      setExistingFiles(files);
    } catch (err) {
      console.error(err);
      setExistingFiles([]);
    } finally {
      setLoadingExistingFiles(false);
    }
  }, [phaseOid, lotOid]);

  const getFileDownloadEndpoint = useCallback((fileOid) => {
    return `/api/files/${fileOid}/download`;
  }, []);

  const handleDownloadExistingFile = useCallback(async (file) => {
    const fileOid = Number(file?.oid);
    if (!Number.isFinite(fileOid) || fileOid <= 0) {
      return;
    }

    setDownloadingFileOid(fileOid);
    try {
      const response = await fetch(getFileDownloadEndpoint(fileOid), { method: 'GET' });
      if (!response.ok) {
        throw new Error('Failed to download file.');
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);

      let downloadName = file?.file_name || `file-${fileOid}`;
      const contentDisposition = response.headers.get('content-disposition') || '';
      const match = contentDisposition.match(/filename="?([^";]+)"?/i);
      if (match && match[1]) {
        downloadName = match[1];
      }

      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = downloadName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      console.error(err);
    } finally {
      setDownloadingFileOid(null);
    }
  }, [getFileDownloadEndpoint]);

  useEffect(() => {
    if (!open) return;
    fetchExistingFiles();
  }, [open, fetchExistingFiles]);

  useEffect(() => {
    let canceled = false;
    const createdUrls = [];

    const loadInlineImages = async () => {
      setInlineImageUrls((prev) => {
        Object.values(prev).forEach((url) => URL.revokeObjectURL(url));
        return {};
      });

      const imageFiles = existingFiles.filter((file) => isImageFile(file));
      if (imageFiles.length === 0) {
        return;
      }

      const results = await Promise.all(
        imageFiles.map(async (file) => {
          const fileOid = Number(file?.oid);
          if (!Number.isFinite(fileOid) || fileOid <= 0) {
            return null;
          }

          try {
            const response = await fetch(getFileDownloadEndpoint(fileOid), { method: 'GET' });
            if (!response.ok) {
              return null;
            }

            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            createdUrls.push(url);
            return { fileOid, url };
          } catch {
            return null;
          }
        })
      );

      if (canceled) {
        createdUrls.forEach((url) => URL.revokeObjectURL(url));
        return;
      }

      const nextUrls = {};
      results.forEach((result) => {
        if (result?.fileOid && result?.url) {
          nextUrls[result.fileOid] = result.url;
        }
      });

      setInlineImageUrls(nextUrls);
    };

    loadInlineImages();

    return () => {
      canceled = true;
      createdUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [existingFiles, getFileDownloadEndpoint, isImageFile]);

  const handleSave = async (phaseRow) => {
    const selectedFiles = finishTaskFilesRef.current?.getFiles() || [];
    await onCompletePhaseClose(phaseRow, selectedFiles);
    finishTaskFilesRef.current?.clearFiles();
    await fetchExistingFiles();
  };

  return (
    <Dialog fullWidth={true} maxWidth='lg' open={open} onClose={onClose} {...dialogProps}>
      <DialogContent>
        <>
          <h2>Finish Task2</h2>
          <h4>{title}</h4>
          <DynamicForm
            procedureDetails={procedureDetailsCompletionInsert}
            onSave={handleSave}
            saveName='Complete'
            crudType='update'
            actionsTopContent={(
              <Stack spacing={1.25}>
                <FileUpload
                  ref={finishTaskFilesRef}
                  inputId='finish-task-file-input'
                  isUploading={isUploading}
                />

                <Box>
                  <Typography sx={{ fontWeight: 600, mb: 0.75 }}>Existing Files</Typography>
                  {loadingExistingFiles ? (
                    <Box sx={{ py: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={18} />
                      <Typography variant='body2' sx={{ color: '#526270' }}>
                        Loading files...
                      </Typography>
                    </Box>
                  ) : existingFiles.length === 0 ? (
                    <Typography variant='body2' sx={{ color: '#526270' }}>
                      No active files uploaded yet.
                    </Typography>
                  ) : (
                    <Stack spacing={0.75}>
                      {existingFiles.map((file) => {
                        const fileOid = file?.oid;
                        const fileName = file?.file_name || `File ${fileOid}`;
                        const createdAt = file?.created_at
                          ? new Date(file.created_at).toISOString().slice(0, 10)
                          : '';

                        return (
                          <Box key={String(fileOid)} sx={{ border: '1px solid #d7e2ec', borderRadius: 1.5, p: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Typography variant='body2' sx={{ color: '#0b426b', fontWeight: 600 }}>
                                {fileName}
                              </Typography>
                              {createdAt ? (
                                <Typography variant='caption' sx={{ color: '#526270' }}>
                                  {createdAt}
                                </Typography>
                              ) : null}
                              <Button
                                size='small'
                                variant='text'
                                onClick={() => handleDownloadExistingFile(file)}
                                disabled={downloadingFileOid === fileOid}
                                sx={{ textTransform: 'none', minWidth: 0, px: 0.5 }}
                              >
                                {downloadingFileOid === fileOid ? 'Downloading...' : 'Download'}
                              </Button>
                            </Box>

                            {inlineImageUrls[fileOid] ? (
                              <Box
                                component='img'
                                src={inlineImageUrls[fileOid]}
                                alt={fileName}
                                sx={{
                                  display: 'block',
                                  mt: 1,
                                  width: '100%',
                                  maxWidth: 360,
                                  maxHeight: 220,
                                  objectFit: 'contain',
                                  borderRadius: 1,
                                  border: '1px solid #e3edf7',
                                  backgroundColor: '#f7fbff',
                                }}
                              />
                            ) : null}
                          </Box>
                        );
                      })}
                    </Stack>
                  )}
                </Box>
              </Stack>
            )}
          ></DynamicForm>
          <h2>Task History</h2>
          <JSONTableEditor
            procedureDetails={procedureDetailsCompletion}
            onSave={onSavePhaseSaveEdit}
            sortOrder={{
              date_complete: 'desc'
            }}
          >
          </JSONTableEditor>
        </>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color='primary'>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FinishTaskDialog;